import React, { useState, useEffect, useCallback } from 'react';
import {
  Form,
  Button,
  Select,
  DatePicker,
  Input,
  Space,
  Checkbox,
  Row,
  Col,
  Radio,
  Alert,
} from 'antd';
import dayjs from 'dayjs';
import {
  ScheduleType,
  MonthlyType,
  DayOfWeek,
  OrdinalWeek,
  ScheduleFormData,
  CollectionConfig,
  MonthlyConfig,
} from '../types';

const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const DAYS_OF_WEEK = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

// Helper function to get day number from day name
const getDayNumber = (dayOfWeek: DayOfWeek): number => {
  const dayMap = {
    [DayOfWeek.SUNDAY]: 0,
    [DayOfWeek.MONDAY]: 1,
    [DayOfWeek.TUESDAY]: 2,
    [DayOfWeek.WEDNESDAY]: 3,
    [DayOfWeek.THURSDAY]: 4,
    [DayOfWeek.FRIDAY]: 5,
    [DayOfWeek.SATURDAY]: 6,
  };
  return dayMap[dayOfWeek];
};

// Function to calculate monthly collection days within a date range
const calculateMonthlyCollectionDays = (
  monthlyConfig: MonthlyConfig,
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs,
): dayjs.Dayjs[] => {
  const collectionDays: dayjs.Dayjs[] = [];

  if (monthlyConfig.type === MonthlyType.ON_DATE) {
    // For ON_DATE type, check each month in the range
    let currentMonth = startDate.startOf('month');
    const endMonth = endDate.endOf('month');

    while (currentMonth.isSameOrBefore(endMonth, 'month')) {
      const collectionDate = currentMonth.date(monthlyConfig.date);
      if (
        collectionDate.isSameOrAfter(startDate, 'day') &&
        collectionDate.isSameOrBefore(endDate, 'day')
      ) {
        collectionDays.push(collectionDate);
      }
      currentMonth = currentMonth.add(1, 'month');
    }
  } else if (monthlyConfig.type === MonthlyType.ON_DAY) {
    // For ON_DAY type, calculate the specific day occurrence
    let currentMonth = startDate.startOf('month');
    const endMonth = endDate.endOf('month');

    while (currentMonth.isSameOrBefore(endMonth, 'month')) {
      const firstDayOfMonth = currentMonth.startOf('month');
      const wantedDayNum = getDayNumber(monthlyConfig.day);

      // Find first occurrence of the wanted day
      let firstOccur = firstDayOfMonth.day(wantedDayNum);
      if (firstOccur.isBefore(firstDayOfMonth)) {
        firstOccur = firstOccur.add(7, 'day');
      }

      let targetDate: dayjs.Dayjs;

      if (monthlyConfig.ordinal === OrdinalWeek.LAST) {
        // Calculate last occurrence
        const lastDay = currentMonth.endOf('month');
        targetDate = lastDay.day(wantedDayNum);
        if (targetDate.isAfter(lastDay)) {
          targetDate = targetDate.subtract(7, 'day');
        }
      } else {
        // Calculate specific occurrence (FIRST, SECOND, THIRD, FOURTH)
        const ordinalMap = {
          [OrdinalWeek.FIRST]: 0,
          [OrdinalWeek.SECOND]: 1,
          [OrdinalWeek.THIRD]: 2,
          [OrdinalWeek.FOURTH]: 3,
        };
        const weeksToAdd = ordinalMap[monthlyConfig.ordinal];
        targetDate = firstOccur.add(weeksToAdd * 7, 'day');
      }

      // Check if the target date is within the schedule range
      if (
        targetDate.isSameOrAfter(startDate, 'day') &&
        targetDate.isSameOrBefore(endDate, 'day')
      ) {
        collectionDays.push(targetDate);
      }

      currentMonth = currentMonth.add(1, 'month');
    }
  }

  return collectionDays;
};

interface ScheduleFormProps {
  form: any;
  onFinish: (values: ScheduleFormData) => void;
  onCancel: () => void;
  submitting: boolean;
  editingSchedule: any;
  currentScheduleType: ScheduleType;
  setCurrentScheduleType: (type: ScheduleType) => void;
  monthlyType: MonthlyType;
  setMonthlyType: (type: MonthlyType) => void;
}

const ScheduleForm: React.FC<ScheduleFormProps> = ({
  form,
  onFinish,
  onCancel,
  submitting,
  editingSchedule,
  currentScheduleType,
  setCurrentScheduleType,
  monthlyType,
  setMonthlyType,
}) => {
  const [validationWarning, setValidationWarning] = useState<string | null>(
    null,
  );

  // Function to validate monthly schedule configuration
  const validateMonthlySchedule = useCallback(() => {
    if (currentScheduleType !== ScheduleType.MONTHLY) {
      setValidationWarning(null);
      return;
    }

    const dateRange = form.getFieldValue('dateRange');
    const monthlyConfig = form.getFieldValue(['collectionConfig', 'monthly']);

    if (!dateRange || !dateRange[0] || !dateRange[1] || !monthlyConfig) {
      setValidationWarning(null);
      return;
    }

    const [startDate, endDate] = dateRange;
    const collectionDays = calculateMonthlyCollectionDays(
      monthlyConfig,
      startDate,
      endDate,
    );

    if (collectionDays.length === 0) {
      const ordinalText = monthlyConfig.ordinal
        ? `${monthlyConfig.ordinal.toLowerCase()} ${monthlyConfig.day}`
        : `${monthlyConfig.date}${getOrdinalSuffix(monthlyConfig.date)}`;

      setValidationWarning(
        `Warning: No collection days found in the selected date range. ` +
          `The schedule is configured for ${ordinalText} of each month, but this doesn't occur within ` +
          `${startDate.format('MMM D, YYYY')} - ${endDate.format('MMM D, YYYY')}. ` +
          `Consider extending the date range or adjusting the schedule configuration.`,
      );
    } else {
      const datesText = collectionDays
        .map((d) => d.format('MMM D, YYYY'))
        .join(', ');
      setValidationWarning(`Collection days in selected range: ${datesText}`);
    }
  }, [currentScheduleType, form]);

  // Helper function to get ordinal suffix
  const getOrdinalSuffix = (num: number): string => {
    const j = num % 10;
    const k = num % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };

  // Watch for form changes to trigger validation
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      validateMonthlySchedule();
    }, 200); // Debounce validation

    return () => clearTimeout(timeoutId);
  }, [currentScheduleType, monthlyType, validateMonthlySchedule]);
  const handleFormSubmit = (values: any) => {
    // Convert RangePicker values to separate startDate and endDate
    const [startDate, endDate] = values.dateRange || [];
    const formData: ScheduleFormData = {
      scheduleType: values.scheduleType,
      collectionConfig: values.collectionConfig,
      startDate,
      endDate,
      description: values.description,
      isActive: values.isActive,
    };
    onFinish(formData);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFormSubmit}
      style={{
        marginBottom: 24,
        padding: 16,
        border: '1px solid #d9d9d9',
        borderRadius: 6,
      }}
    >
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name="scheduleType"
            label="Schedule Type"
            rules={[{ required: true, message: 'Please select schedule type' }]}
          >
            <Select
              onChange={(value: ScheduleType) => {
                setCurrentScheduleType(value);
                // Reset collection config when schedule type changes
                const newConfig: CollectionConfig = {};
                if (value === ScheduleType.WEEKLY) {
                  newConfig.weekly = { days: [] };
                } else if (value === ScheduleType.BIWEEKLY) {
                  newConfig.biWeekly = { week1Days: [], week2Days: [] };
                } else if (value === ScheduleType.MONTHLY) {
                  setMonthlyType(MonthlyType.ON_DATE);
                  newConfig.monthly = {
                    type: MonthlyType.ON_DATE,
                    date: 1,
                  };
                }
                form.setFieldValue('collectionConfig', newConfig);
              }}
            >
              <Option value={ScheduleType.WEEKLY}>Weekly</Option>
              <Option value={ScheduleType.BIWEEKLY}>Fortnightly</Option>
              <Option value={ScheduleType.MONTHLY}>Monthly</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {/* Dynamic Collection Configuration */}
      {currentScheduleType === ScheduleType.WEEKLY && (
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name={['collectionConfig', 'weekly', 'days']}
              label="Collection Days"
              rules={[
                {
                  required: true,
                  message: 'Please select collection days',
                },
              ]}
            >
              <Checkbox.Group options={DAYS_OF_WEEK} />
            </Form.Item>
          </Col>
        </Row>
      )}

      {currentScheduleType === ScheduleType.BIWEEKLY && (
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name={['collectionConfig', 'biWeekly', 'week1Days']}
              label="Week 1 Collection Days"
              rules={[
                {
                  required: true,
                  message: 'Please select week 1 collection days',
                },
              ]}
            >
              <Checkbox.Group options={DAYS_OF_WEEK} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={['collectionConfig', 'biWeekly', 'week2Days']}
              label="Week 2 Collection Days"
              rules={[
                {
                  required: true,
                  message: 'Please select week 2 collection days',
                },
              ]}
            >
              <Checkbox.Group options={DAYS_OF_WEEK} />
            </Form.Item>
          </Col>
        </Row>
      )}

      {currentScheduleType === ScheduleType.MONTHLY && (
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="Monthly Collection Type">
              <Radio.Group
                value={monthlyType}
                onChange={(e) => {
                  const newType = e.target.value;
                  setMonthlyType(newType);
                  const newConfig: MonthlyConfig =
                    newType === MonthlyType.ON_DATE
                      ? { type: MonthlyType.ON_DATE, date: 1 }
                      : {
                          type: MonthlyType.ON_DAY,
                          ordinal: OrdinalWeek.FIRST,
                          day: DayOfWeek.MONDAY,
                        };
                  form.setFieldValue(
                    ['collectionConfig', 'monthly'],
                    newConfig,
                  );
                }}
              >
                <Radio value={MonthlyType.ON_DATE}>On specific date</Radio>
                <Radio value={MonthlyType.ON_DAY}>On specific day</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          {monthlyType === MonthlyType.ON_DATE && (
            <Col span={12}>
              <Form.Item
                name={['collectionConfig', 'monthly', 'date']}
                label="Date of Month"
                rules={[{ required: true, message: 'Please select date' }]}
              >
                <Select
                  placeholder="Select date (1-31)"
                  onChange={() => {
                    setTimeout(() => validateMonthlySchedule(), 50);
                  }}
                >
                  {Array.from({ length: 31 }, (_, i) => (
                    <Option key={i + 1} value={i + 1}>
                      {i + 1}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}

          {monthlyType === MonthlyType.ON_DAY && (
            <>
              <Col span={12}>
                <Form.Item
                  name={['collectionConfig', 'monthly', 'ordinal']}
                  label="Week of Month"
                  rules={[{ required: true, message: 'Please select week' }]}
                >
                  <Select
                    placeholder="Select week"
                    onChange={() => {
                      setTimeout(() => validateMonthlySchedule(), 50);
                    }}
                  >
                    <Option value={OrdinalWeek.FIRST}>First</Option>
                    <Option value={OrdinalWeek.SECOND}>Second</Option>
                    <Option value={OrdinalWeek.THIRD}>Third</Option>
                    <Option value={OrdinalWeek.FOURTH}>Fourth</Option>
                    <Option value={OrdinalWeek.LAST}>Last</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={['collectionConfig', 'monthly', 'day']}
                  label="Day of Week"
                  rules={[{ required: true, message: 'Please select day' }]}
                >
                  <Select
                    placeholder="Select day"
                    onChange={() => {
                      setTimeout(() => validateMonthlySchedule(), 50);
                    }}
                  >
                    {DAYS_OF_WEEK.map((day) => (
                      <Option key={day} value={day}>
                        {day}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
      )}

      {/* Validation Warning for Monthly Schedules */}
      {currentScheduleType === ScheduleType.MONTHLY && validationWarning && (
        <Row gutter={16}>
          <Col span={24}>
            <Alert
              message={validationWarning}
              type={
                validationWarning.startsWith('Warning:') ? 'warning' : 'info'
              }
              showIcon
              style={{ marginBottom: 16 }}
            />
          </Col>
        </Row>
      )}

      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name="dateRange"
            label="Schedule Period"
            rules={[
              { required: true, message: 'Please select schedule period' },
            ]}
          >
            <RangePicker
              style={{ width: '100%' }}
              placeholder={['Start Date', 'End Date']}
              onChange={() => {
                setTimeout(() => validateMonthlySchedule(), 50);
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="description" label="Description">
        <TextArea rows={3} placeholder="Optional description" />
      </Form.Item>

      <Form.Item name="isActive" valuePropName="checked">
        <Checkbox>Active</Checkbox>
      </Form.Item>

      <Form.Item>
        <Space>
          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            disabled={submitting}
          >
            {editingSchedule ? 'Update' : 'Create'} Schedule
          </Button>
          <Button onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default ScheduleForm;
